import {
  AccountsService,
  GetPricingTableResponse,
  PlanName,
  PricingTable,
} from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { navigateInternal } from 'base/navigation';
import { AvatarGroup } from 'base/ui/avatar_group';
import { Banner } from 'base/ui/banner';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { RedErrorText } from 'base/ui/form';
import { IconCheck } from 'base/ui/icons';
import { Grow, Inline, Rows } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { DividerLine } from 'base/ui/panel';
import { TickRow } from 'base/ui/tick_row';
import {
  TextMedium,
  TextRegular,
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  TextTinyLight,
  Title1,
  Title6,
} from 'base/ui/typography';
import { mergeSearchParams } from 'base/url';
import {
  AcceptPlanDialogUrlState,
  AcceptPlanOfferDialog,
} from 'blocks/auth/accept_plan_offer';
import { CancelPlanDialog } from 'blocks/auth/cancel_plan';
import {
  formatCentAmount,
  OpenStripeBillingButton,
  PauseActiveBanner,
  PaymentProblemBanner,
  ReactivatePlanBanner,
} from 'blocks/auth/plan_buttons';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { getBillingInterval, getPlanName } from 'blocks/billing/plan_utils';
import { getCurrentPlanKey } from 'blocks/billing/PricingPage';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import adamAvatar from './adam.webp';
import { CouponBanners } from './coupon_banners';
import danAvatar from './dan.webp';
import styles from './manage_plan.css';
import { manageAccountRouteToUrl } from './routes';
import { DiscountChip } from './ui';

export function ManagePlan({
  accountsService,
  accountStore,
  analytics,
  dialog,
  setDialog,
}: {
  accountsService: AccountsService;
  accountStore: AccountStore;
  analytics: AnalyticsService;
  dialog: AcceptPlanDialogUrlState | undefined;
  setDialog: (dialog: AcceptPlanDialogUrlState | undefined) => void;
}) {
  const [res, setRes] = React.useState<GetPricingTableResponse | undefined>();
  const [error, setError] = React.useState<Error | undefined>();

  React.useEffect(() => {
    accountsService
      .getPricingTable({ includeNextPayment: true })
      .then((res) => setRes(res))
      .catch(setError);
  }, [accountsService]);

  /* Most users can manage their own billing so we show this while loading */
  const canManageBilling = res == null || res.canManageBilling;

  const canTogglePeriod =
    res != null &&
    ((dialog?.planKey?.startsWith('pro') &&
      res.table.proMonthly?.status === 'available') ||
      (dialog?.planKey?.startsWith('starter') &&
        res.table.starterMonthly?.status === 'available'));

  const handlePlanChange = useCallback(
    (planChosen: keyof PricingTable | undefined, hasCoupon: boolean) => {
      if (planChosen) {
        analytics.track({
          event: 'upgrade_flow:payment_form_submit',
          plan_name: getPlanName(planChosen),
          billing_interval: getBillingInterval(planChosen),
          has_coupon: hasCoupon,
        });
      }
      return window.history.pushState(
        {},
        '',
        mergeSearchParams(
          planChosenToRedirectUrl(planChosen),
          planChosen != null
            ? {
                [PLAN_UPGRADED_SEARCH_PARAM]: planChosen,
              }
            : {},
        ),
      );
    },
    [analytics],
  );

  return (
    <Rows spacing={32}>
      <Title1>Manage Plan</Title1>
      <Rows spacing={24}>
        <Title6>Current Plan</Title6>
        <RedErrorText error={error} />
        <CouponBanners
          accountsService={accountsService}
          analytics={analytics}
          analyticsLocation="manage-plan"
        />
        {!canManageBilling && <TeamMemberBanner />}
        {res?.trialEndDate && canManageBilling && (
          <TrialWillEndBanner
            trialEndDate={res.trialEndDate}
            currentPlanKey={getCurrentPlanKey(res.table) || 'free'}
            setDialog={setDialog}
          />
        )}
        {res?.cancelsAt && canManageBilling && (
          <ReactivatePlanBanner
            variant="grey3"
            cancelsAt={res.cancelsAt}
            resumption={res.resumption}
            accountsService={accountsService}
          />
        )}
        {res?.cancelsAt == null && res?.resumption && canManageBilling && (
          <PauseActiveBanner
            resumption={res.resumption}
            accountsService={accountsService}
          />
        )}
        {res?.hasPaymentProblem && canManageBilling && (
          <PaymentProblemBanner
            variant="grey3"
            accountsService={accountsService}
          />
        )}
        <div className={styles.card}>
          {res ? (
            <PlanCardContent
              res={res}
              accountsService={accountsService}
              accountStore={accountStore}
              setDialog={setDialog}
            />
          ) : (
            <LoadingDots aspectRatio={5 / 1} />
          )}
        </div>
      </Rows>
      {canManageBilling && (
        <>
          <DividerLine />
          <Rows spacing={24}>
            <Title6>Billing</Title6>
            <BillingDetailsTable res={res} />
            <Rows spacing={16} xalign="start">
              <TextRegularLight>
                Manage your billing email address, payment method and view
                invoices with your account email.
              </TextRegularLight>
              <OpenStripeBillingButton
                accountService={accountsService}
                label="Manage Billing"
              />
            </Rows>
          </Rows>
        </>
      )}
      {dialog && (
        <AcceptPlanOfferDialog
          urlState={dialog}
          setUrlState={setDialog}
          accountsService={accountsService}
          accountStore={accountStore}
          onClose={() => setDialog(undefined)}
          redirectOnSuccess={handlePlanChange}
          canChangePeriod={canTogglePeriod}
        />
      )}
    </Rows>
  );
}

function TeamMemberBanner() {
  const teamUrl = manageAccountRouteToUrl({
    path: 'manage-account/team',
  });
  return (
    <Banner
      variant="grey3"
      actions={
        <Button
          label="View team"
          href={teamUrl}
          onClick={(e) => {
            e.preventDefault();
            window.history.pushState(null, '', teamUrl);
          }}
        />
      }
    >
      <Inline spacing={8}>
        <TextSmall>Your plan is part of a team</TextSmall>
        <TextSmallLight>
          Get in touch with an admin if you need to manage your plan.
        </TextSmallLight>
      </Inline>
    </Banner>
  );
}

function BillingDetailsTable({
  res,
}: {
  res: GetPricingTableResponse | undefined;
}) {
  if (!res) {
    return null;
  }
  const currentPlanKey = getCurrentPlanKey(res.table) || 'free';
  const currentPlan = res.table[currentPlanKey];
  if (!currentPlan || !res.nextPayment) {
    return null;
  }

  return (
    <div className={styles.fourColumn}>
      <Rows spacing={8}>
        <TextSmallLight color="grey">Next Payment</TextSmallLight>
        <TextRegularLight>{res.nextPayment.date}</TextRegularLight>
      </Rows>
      <Rows spacing={8}>
        <TextSmallLight color="grey">Billing Cycle</TextSmallLight>
        <TextRegularLight>
          {PLAN_IS_YEARLY[currentPlanKey] ? 'Yearly' : 'Monthly'}
        </TextRegularLight>
      </Rows>
      <Rows spacing={8}>
        <TextSmallLight color="grey">Seats</TextSmallLight>
        <TextRegularLight>{res.nextPayment.seats}</TextRegularLight>
      </Rows>
      <Rows spacing={8}>
        <TextSmallLight color="grey">Total</TextSmallLight>
        <TextRegularLight>
          {formatCentAmount(res.nextPayment.amountDueCents)}
        </TextRegularLight>
      </Rows>
    </div>
  );
}

function PlanCardContent({
  res,
  accountsService,
  accountStore,
  setDialog,
}: {
  res: GetPricingTableResponse;
  accountsService: AccountsService;
  accountStore: AccountStore;
  setDialog: (dialog: AcceptPlanDialogUrlState | undefined) => void;
}) {
  const currentPlanKey = getCurrentPlanKey(res.table) || 'free';
  const currentPlan = res.table[currentPlanKey];
  if (!currentPlan) {
    return (
      <TextRegular>
        Unknown Plan. Please contact support@relume.io for help.
      </TextRegular>
    );
  }
  const benefits =
    BENEFITS_BY_PLAN_KEY[
      accountStore.member?.legacyPlanName === PlanName.PRO &&
      accountStore.member?.planName === PlanName.TEAM
        ? 'proLegacy'
        : currentPlanKey
    ];

  return (
    <Rows spacing={16}>
      <div
        className={classNames(
          styles.twoColumn,
          res?.hasPaymentProblem && styles.isFaded,
        )}
      >
        <Inline spacing={0}>
          <Grow>
            <Rows spacing={16}>
              <TextRegular>
                {(currentPlanKey && PLAN_NAME_BY_KEY[currentPlanKey]) ||
                  'Unknown Plan'}
              </TextRegular>
              {res.canManageBilling && (
                <TextSmallLight>
                  <TextRegular tag="span">
                    {formatCentAmount(currentPlan.centsPerMemberPerMonth, {
                      wholeDollars: true,
                    })}
                  </TextRegular>{' '}
                  per member-month
                  {PLAN_IS_YEARLY[currentPlanKey] ? ', billed yearly' : ''}
                </TextSmallLight>
              )}
            </Rows>
          </Grow>
          {res.canManageBilling &&
            res.activeCoupon &&
            res.activeCoupon.discountPercent > 0 && (
              <DiscountChip
                percent={res.activeCoupon.discountPercent}
                tooltip={`Code: ${res.activeCoupon.couponCode}`}
              />
            )}
        </Inline>
        <Rows spacing={8}>
          <TextSmall>Included in plan</TextSmall>
          {benefits.map((b) => (
            <TickRow size="small" key={b}>
              {b}
            </TickRow>
          ))}
        </Rows>
      </div>
      {res.canManageBilling && (res.resumption || !res.cancelsAt) && (
        <>
          <DividerLine />
          <Inline spacing={16}>
            <Button
              href={authRouteToUrl({
                path: 'upgrade',
                context: { redirect: getCurrentLocationForRedirect() },
              })}
              label={
                currentPlanKey === 'free' ? 'Upgrade' : 'Upgrade or change plan'
              }
              variant="primary"
              onClick={navigateInternal}
            />
            {currentPlanKey !== 'free' && (
              <CancelPlanButton
                accountsService={accountsService}
                setDialog={setDialog}
                table={res.table}
              />
            )}
          </Inline>
        </>
      )}
    </Rows>
  );
}

function CancelPlanButton({
  accountsService,
  setDialog,
  table,
}: {
  accountsService: AccountsService;
  setDialog: (dialog: AcceptPlanDialogUrlState | undefined) => void;
  table: PricingTable;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button
        onClick={() => {
          accountsService.getChurnkeyHash().then(
            ({ hash, customerId, subscriptionId }) => {
              window.churnkey.init('show', {
                subscriptionId,
                customerId,
                authHash: hash,
                appId: 'mdnpklk56',
                mode: 'live',
                provider: 'stripe',
              });
            },
            (error) => {
              console.error(
                'Error fetching churnkey hash - falling back to old method',
                error,
              );
              setOpen(true);
            },
          );
        }}
        label="Cancel plan"
        background="transparent"
        disabled={table.free.status === 'unavailable'}
        tooltip={
          table.free.status === 'unavailable' ? table.free.reason : undefined
        }
      />
      {open && (
        <CancelPlanDialog
          accountsService={accountsService}
          onClose={() => setOpen(false)}
          onDone={() => window.location.reload()}
          onUpsell={(state) => {
            setOpen(false);
            setDialog(state);
          }}
        />
      )}
    </>
  );
}

const PLAN_NAME_BY_KEY: Record<keyof PricingTable, string> = {
  free: 'Free Plan',
  starterMonthly: 'Starter Plan (Monthly)',
  starterYearly: 'Starter Plan (Yearly)',
  proMonthly: 'Pro Plan (Monthly)',
  proYearly: 'Pro Plan (Yearly)',
  teamMonthly: 'Team Plan (Monthly)',
  teamYearly: 'Team Plan (Yearly)',
};

const PLAN_IS_YEARLY: Record<keyof PricingTable, boolean> = {
  free: false,
  starterMonthly: false,
  starterYearly: true,
  proMonthly: false,
  proYearly: true,
  teamMonthly: false,
  teamYearly: true,
};

const FREE_BENEFITS = [
  `1 project`,
  `1 page`,
  `Share - Read-only link`,
  `Export - Figma only`,
];
const STARTER_BENEFITS = [
  `1 project`,
  `5 pages`,
  `Share - Commenting`,
  `Export - Figma, Webflow & React`,
  `1,000+ components`,
];
const PRO_BENEFITS = [
  `Unlimited projects`,
  `Unlimited pages`,
  `Share - Commenting`,
  `Export - Figma, Webflow & React`,
  `1,000+ components`,
];

const TEAM_BENEFITS = [
  `3 Users included`,
  `Each user with Pro features`,
  `Team workspace`,
  'Branded sharing',
];

const PRO_LEGACY_BENEFITS = [`Each user with Pro features`, `Team workspace`];

const BENEFITS_BY_PLAN_KEY: Record<keyof PricingTable | 'proLegacy', string[]> =
  {
    free: FREE_BENEFITS,
    starterMonthly: STARTER_BENEFITS,
    starterYearly: STARTER_BENEFITS,
    proMonthly: PRO_BENEFITS,
    proYearly: PRO_BENEFITS,
    proLegacy: PRO_LEGACY_BENEFITS,
    teamMonthly: TEAM_BENEFITS,
    teamYearly: TEAM_BENEFITS,
  };

function TrialWillEndBanner({
  currentPlanKey,
  trialEndDate,
  setDialog,
}: {
  currentPlanKey: keyof PricingTable;
  trialEndDate: string;
  setDialog: (dialog: AcceptPlanDialogUrlState | undefined) => void;
}) {
  if (currentPlanKey === 'free') {
    return null;
  }
  return (
    <Banner
      variant="primary"
      actions={
        <Button
          label="Upgrade now"
          onClick={() =>
            setDialog({ planKey: currentPlanKey, endTrialNow: true })
          }
        />
      }
    >
      <TextSmall>Your free trial will end on {trialEndDate}</TextSmall>
    </Banner>
  );
}

const FOUNDER_AVATARS = [
  {
    id: 0,
    color: '#000000',
    self: false,
    user: {
      firstName: 'Adam',
      lastName: 'Mura',
      avatar: {
        height: 32,
        width: 32,
        files: [{ size: 32, url: adamAvatar }],
      },
    },
  },
  {
    id: 1,
    color: '#000000',
    self: false,
    user: {
      firstName: 'Dan',
      lastName: 'Anisse',
      avatar: {
        height: 32,
        width: 32,
        files: [{ size: 32, url: danAvatar }],
      },
    },
  },
];

const UPGRADE_SUCCESS = {
  [PlanName.STARTER]: {
    body: 'We are deeply grateful for your support—it truly means the world to us and enables us to maintain our momentum. We welcome you as an official Lumer today!',
    title: 'Starter',
    benefits: STARTER_BENEFITS,
  },
  [PlanName.PRO]: {
    body: 'We are thrilled to see that Relume has become a part of your workflow. Thank you for your continued support!',
    title: 'Pro',
    benefits: PRO_BENEFITS,
  },
  [PlanName.TEAM]: {
    body: 'We are thrilled to see that Relume has become a part of your workflow for you and your team. Thank you for your continued support!',
    title: 'Team',
    benefits: TEAM_BENEFITS,
  },
};

export const PLAN_UPGRADED_SEARCH_PARAM = 'plan-upgraded';

export const UpgradeThankYouDialog = observer(function _UpgradeThankYouDialog({
  onClose,
  planName,
}: {
  onClose: () => void;
  planName: 'starter' | 'creator' | 'team';
}) {
  return (
    <Dialog
      title="Plan upgraded"
      onClose={onClose}
      buttons={<Button variant="primary" label="Let's go" onClick={onClose} />}
      width="narrow"
    >
      <TextRegularLight color="grey">
        {UPGRADE_SUCCESS[planName].body}
      </TextRegularLight>
      <Inline xalign="space-between">
        <Rows spacing={0}>
          <TextSmall>Dan & Adam</TextSmall>
          <TextTinyLight>Relume co-founders</TextTinyLight>
        </Rows>
        <AvatarGroup
          avatars={FOUNDER_AVATARS}
          background="white"
          maxVisible={2}
          overlap
          size="large"
        />
      </Inline>
      <DividerLine />
      <TextMedium>
        Relume{' '}
        <TextMedium tag="span" color="gradient">
          {UPGRADE_SUCCESS[planName].title}
        </TextMedium>
      </TextMedium>
      <div className={styles.upgradeFeatureListContainer}>
        <Rows spacing={8}>
          {UPGRADE_SUCCESS[planName].benefits.map((benefit, i) => (
            <Inline key={i} yalign="center" spacing={10}>
              <IconCheck className={styles.checkIcon} />
              <TextSmall>{benefit}</TextSmall>
            </Inline>
          ))}
        </Rows>
      </div>
    </Dialog>
  );
});

export function planChosenToRedirectUrl(planChosen?: string) {
  switch (planChosen) {
    case 'teamMonthly':
    case 'teamYearly':
      return manageAccountRouteToUrl({
        path: 'manage-account/team',
      });
    default:
      return '/dashboard';
  }
}
